import { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Table2, Trash2, Type, Video } from 'lucide-react';
import { TASK_GROUP_BY, TASK_METRICS, type SectionBlock, type SectionBlockType, type WorkspaceSection } from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { TextField } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import {
  useCreateBlock, useCreateSection, useDeleteBlock, useDeleteSection, useReorderBlocks, useReorderSections, useSectionBlocks,
  useSections, useUpdateBlock, useUpdateSection,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { WidgetBoard } from '../widgets/ConfigurableChart.tsx';
import { BlockList, NewBlockForm } from './SectionBlocks.tsx';

const EMOJI_CHOICES = ['📁', '🎓', '🤝', '🎯', '💡', '📣', '🛠️', '⭐️', '📊', '🗓️'];

/** A custom section's own tab content: its content blocks (text, tables, video) and its charts (task data,
 * reusing the same generic widget system as Finance/CRM). */
export function CustomSectionView({ workspaceId, section, canManage }: { workspaceId: string; section: WorkspaceSection; canManage: boolean }) {
  const { orgId } = useOrgCtx();
  const blocks = useSectionBlocks(orgId, workspaceId, section.id);
  const create = useCreateBlock(orgId, workspaceId, section.id);
  const update = useUpdateBlock(orgId, workspaceId, section.id);
  const reorder = useReorderBlocks(orgId, workspaceId, section.id);
  const del = useDeleteBlock(orgId, workspaceId, section.id);
  const [adding, setAdding] = useState<SectionBlockType | null>(null);
  const toast = useToast();

  const move = (i: number, dir: -1 | 1) => {
    const list = blocks.data ?? [];
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const ids = list.map((b) => b.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  return (
    <>
      <div className="section-head">
        <h2 className="section-title">{section.icon} {section.name}</h2>
      </div>

      {blocks.data && blocks.data.length > 0 && (
        <BlockList
          blocks={blocks.data} canManage={canManage} onMove={move}
          onUpdate={(id, patch) => update.mutate({ id, patch }, { onError: () => toast('Modifica non riuscita') })}
          onDelete={(b) => { if (confirm(`Eliminare “${b.title ?? (b.type === 'text' ? 'questo testo' : b.type === 'table' ? 'questa tabella' : 'questo video')}”?`)) del.mutate(b.id); }}
        />
      )}

      {canManage && (
        adding ? (
          <NewBlockForm
            type={adding} onCancel={() => setAdding(null)}
            onSubmit={(v) => create.mutate(v, { onSuccess: () => setAdding(null), onError: () => toast('Creazione non riuscita') })}
            pending={create.isPending}
          />
        ) : (
          <div className="block-type-picker">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding('text')}><Type size={15} /> Testo</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding('table')}><Table2 size={15} /> Tabella</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding('video')}><Video size={15} /> Video</button>
          </div>
        )
      )}

      <h3 className="subhead">Grafico task</h3>
      <WidgetBoard workspaceId={workspaceId} module="tasks" sectionId={section.id} metrics={[...TASK_METRICS]} groupBys={[...TASK_GROUP_BY]} canEdit={canManage} />
    </>
  );
}

/** Create, rename, reorder and delete custom sections. Opened from the tab bar's "Sezioni" button. */
export function SectionsManagerSheet({ open, onClose, workspaceId, sections }: { open: boolean; onClose: () => void; workspaceId: string; sections: WorkspaceSection[] }) {
  const { orgId } = useOrgCtx();
  const create = useCreateSection(orgId, workspaceId);
  const update = useUpdateSection(orgId, workspaceId);
  const reorder = useReorderSections(orgId, workspaceId);
  const del = useDeleteSection(orgId, workspaceId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(EMOJI_CHOICES[0]!);
  const [renaming, setRenaming] = useState<string | null>(null);

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const ids = sections.map((s) => s.id);
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  return (
    <Sheet open={open} onClose={onClose} title="Sezioni personalizzate" variant="center" labelledBy="sections-title">
      <p className="hint" style={{ marginTop: 0 }}>Aggiungi schede su misura per questo spazio (es. Formazione, Obiettivi) con grafici sulle task.</p>
      <div className="card list">
        {sections.map((s, i) => (
          <div key={s.id} className="section-row">
            {renaming === s.id ? (
              <input
                className="input" autoFocus defaultValue={s.name} style={{ flex: 1 }}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.name) update.mutate({ id: s.id, patch: { name: v } }); setRenaming(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenaming(null); }}
              />
            ) : (
              <button type="button" className="grow" style={{ textAlign: 'left' }} onClick={() => setRenaming(s.id)}>{s.icon} {s.name}</button>
            )}
            <span className="order-btns">
              <button type="button" className="icon-btn muted" aria-label="Sposta su" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={16} /></button>
              <button type="button" className="icon-btn muted" aria-label="Sposta giù" disabled={i === sections.length - 1} onClick={() => move(i, 1)}><ArrowDown size={16} /></button>
            </span>
            <button type="button" className="icon-btn muted" aria-label={`Elimina ${s.name}`} onClick={() => {
              if (confirm(`Eliminare la sezione “${s.name}”? Anche i suoi grafici andranno persi.`)) del.mutate(s.id, { onSuccess: () => toast(`${s.name} eliminata`) });
            }}><Trash2 size={16} /></button>
          </div>
        ))}
        {sections.length === 0 && <p className="hint" style={{ padding: 16, margin: 0 }}>Nessuna sezione ancora.</p>}
      </div>

      <h3 className="subhead">Nuova sezione</h3>
      <form className="form" onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        create.mutate({ name: name.trim(), icon }, { onSuccess: () => { setName(''); toast('Sezione creata'); } });
      }}>
        <div className="chips" role="group" aria-label="Icona">
          {EMOJI_CHOICES.map((e) => (
            <button key={e} type="button" className="chip" aria-pressed={icon === e} onClick={() => setIcon(e)}>{e}</button>
          ))}
        </div>
        <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Formazione" required />
        <button type="submit" className="btn btn-primary" disabled={!name.trim() || create.isPending}><Plus size={16} /> Aggiungi sezione</button>
      </form>
    </Sheet>
  );
}
